import { JOB_STAGES, type JobStageId, type Readiness } from '../api'
import { Icon } from './Icon'
import type { ArtifactId } from './artifacts'

// StageRail — the spine. A job's whole life, concept → close-out, always
// visible, with the current stage marked and everything before it done.
//
// It exists because the product had every stage but showed none of them: you
// could tell what to do NEXT (the action ladder) but never where you WERE, what
// came after, or where the tools for this moment lived. The rail is the map;
// clicking a stage opens the artifact that stage is about, which is what makes
// the depth explorable instead of hidden.

// Which artifact each stage is "about" — clicking a stage opens it. Stages
// without their own artifact (concept, accepted) return null and stay inert.
const STAGE_ARTIFACT: Record<JobStageId, ArtifactId | null> = {
  concept: null,
  scoping: 'questions',
  pricing: 'bid',
  proposed: 'bid',
  accepted: null,
  building: 'run',
  closing: 'accuracy',
}

export function StageRail({
  readiness,
  onOpenArtifact,
}: {
  readiness: Readiness | null
  onOpenArtifact: (id: ArtifactId) => void
}) {
  const current = readiness?.job_stage
  // Before readiness lands the rail used to render nothing, so a ~56px bar
  // appeared at the top of the console a beat later and shoved everything below
  // it down — one layout shift worth 0.29 CLS, the single largest on the page.
  // The spine is the same seven stages for every job, so it can be drawn
  // immediately and simply not know where you are yet: the space is reserved,
  // and the map shows up sooner.
  const idx = current ? JOB_STAGES.findIndex((s) => s.id === current) : -1
  const pending = !current

  // The client's answer is the one piece of state the operator can't discover by
  // looking at the plan, so it gets said outright rather than implied by a dot.
  const client = readiness?.client_status
  const clientLine =
    client === 'accepted'
      ? `✓ ${readiness?.client_name || 'The client'} accepted`
      : client === 'declined'
        ? `${readiness?.client_name || 'The client'} declined — revise and re-send`
        : client === 'open'
          ? 'Sent — waiting on the client'
          : null

  return (
    <div className={`stage-rail${pending ? ' pending' : ''}`} aria-label="Job progress" aria-busy={pending || undefined}>
      <ol className="stage-list">
        {JOB_STAGES.map((s, i) => {
          const state = pending ? 'ahead' : i < idx ? 'done' : i === idx ? 'now' : 'ahead'
          const art = pending ? null : STAGE_ARTIFACT[s.id]
          const label = (
            <>
              {/* Every state reserves the SAME glyph slot. Rendering nothing for
                  an "ahead" pill made the pill narrower, so when readiness landed
                  and ticks/dots appeared the whole row re-flowed sideways and
                  unwrapped a line — a layout shift measured at dx12/dy-26. */}
              <span className={`stage-slot ${state}`} aria-hidden>
                {state === 'done' ? <Icon name="check" size={13} /> : state === 'now' ? <i /> : null}
              </span>
              <span className="stage-name">{s.label}</span>
            </>
          )
          return (
            <li key={s.id} className={`stage ${state}`}>
              {art ? (
                <button
                  className="stage-btn"
                  onClick={() => onOpenArtifact(art)}
                  title={`${s.label} — ${s.note}`}
                  aria-current={state === 'now' ? 'step' : undefined}
                >
                  {label}
                </button>
              ) : (
                <span className="stage-btn static" aria-current={state === 'now' ? 'step' : undefined}>
                  {label}
                </span>
              )}
            </li>
          )
        })}
      </ol>
      {clientLine ? (
        <span className={`stage-client ${client}`}>{clientLine}</span>
      ) : (
        <span className="stage-here"><span className="stage-here-lead">Now</span> {JOB_STAGES[idx]?.note}</span>
      )}
    </div>
  )
}
